import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { MailCheck, MailX, ArrowLeft } from "lucide-react";
import { API } from "@/lib/config";
import { Seo } from "@/components/Seo";

const COPY = {
  confirm: { path: "/subscriptions/confirm", title: "Subscription confirmed", Icon: MailCheck, auto: true,
    ok: (email) => `You're on the crew list as ${email}. Welcome aboard — a welcome email is on its way.`,
    seo: "Confirm subscription — Captain's Cut" },
  unsubscribe: { path: "/subscriptions/unsubscribe", title: "You're unsubscribed", Icon: MailX, auto: false,
    idleTitle: "UNSUBSCRIBE FROM CREW EMAILS",
    idle: "Click the button below and you will receive no more crew emails from Captain's Cut. Verification codes you request yourself still work.",
    button: "Unsubscribe",
    ok: (email) => `${email} will receive no more crew emails from Captain's Cut. Verification codes you request yourself still work.`,
    seo: "Unsubscribe — Captain's Cut" },
};

/** Email actions from token links (no login). Confirm acts on load; unsubscribe waits for an explicit click
 *  so link-scanning mail gateways can't unsubscribe people by prefetching the URL. */
export default function SubscriptionAction({ mode }) {
  const { hash, pathname } = useLocation();
  const cfg = COPY[mode];
  const token = new URLSearchParams(hash.replace(/^#/, "")).get("token");
  const [state, setState] = useState({ status: cfg.auto ? "working" : "idle" });
  const done = useRef(false);

  const run = () => {
    if (done.current) return;
    done.current = true;
    setState({ status: "working" });
    axios.post(`${API}${cfg.path}`, { token })
      .then((r) => setState({ status: "ok", email: r.data.email }))
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        setState({ status: "error", message: typeof detail === "string" ? detail : "This link is invalid or has already been used." });
      });
  };

  useEffect(() => {
    if (!token) { setState({ status: "error", message: "This link is missing its token. Please use the link from your email." }); return; }
    if (cfg.auto) run();
  }, [token, cfg.auto]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = { idle: cfg.idleTitle, working: "ONE MOMENT…", ok: cfg.title.toUpperCase(), error: "LINK NOT VALID" }[state.status];
  const message = { idle: cfg.idle, working: "Updating your preferences.", ok: state.status === "ok" && cfg.ok(state.email), error: state.message }[state.status];

  return (
    <div className="min-h-screen bg-[#070A0F] text-[#F7F8F9] grain flex items-center justify-center px-5" data-testid={`${mode}-page`}>
      <Seo title={cfg.seo} description="Manage your Captain's Cut email subscription." path={pathname} noindex />
      <div className="w-full max-w-md border border-[#D4AF37]/30 bg-[#0B111B] p-8 md:p-10">
        <p className="text-[10px] tracking-[0.45em] uppercase text-[#D4AF37]">Captain's Cut · Email</p>
        <cfg.Icon size={28} className="text-[#E8C15A] mt-6" />
        <h1 className="font-display text-2xl md:text-3xl tracking-[0.08em] text-[#F5F3EF] mt-4" data-testid={`${mode}-title`}>{title}</h1>
        <p className="text-sm text-[#94A3B8] leading-relaxed mt-4" data-testid={`${mode}-message`}>{message}</p>
        {state.status === "idle" && (
          <button type="button" onClick={run} data-testid={`${mode}-button`}
            className="mt-8 w-full border border-[#D4AF37] bg-[#D4AF37] text-[#070A0F] text-[11px] tracking-[0.3em] uppercase py-3.5 font-semibold hover:bg-[#E8C15A] hover:border-[#E8C15A] transition-colors">
            {cfg.button}
          </button>
        )}
        <Link to="/" className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#94A3B8] hover:text-[#E8C15A] transition-colors" data-testid={`${mode}-back-home`}>
          <ArrowLeft size={12} /> Back to the site
        </Link>
      </div>
    </div>
  );
}
